import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { userService } from '@/services/userService';
import { roleService } from '@/services/roleService';
import type { CreateUserRequest, UpdateUserRequest, UserFilterParams } from '@/types/user';

export interface UseUsersParams {
  page: number;
  pageSize: number;
  search: string;
  roles: string[];
  department: 'OMS' | 'AMS' | 'EPC' | null;
  status: 'active' | 'inactive' | null;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

function toFilterParams(p: UseUsersParams): UserFilterParams {
  const params: UserFilterParams = {
    page: p.page,
    limit: p.pageSize,
    sortBy: `${p.sortField}:${p.sortOrder}`,
  };
  if (p.search) params.name = p.search;
  if (p.roles.length) params.roles = p.roles.join(',');
  if (p.department) params.department = p.department;
  if (p.status) params.status = p.status;
  return params;
}

const USERS_ROOT = ['users'] as const;

export function useUsersQuery(p: UseUsersParams) {
  const params = toFilterParams(p);
  const query = useQuery({
    queryKey: [...USERS_ROOT, params],
    queryFn: () => userService.getUsers(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  return { ...query, users: query.data?.users ?? [], totalDocs: query.data?.totalDocs ?? 0 };
}

export function useRolesForDdl() {
  return useQuery({
    queryKey: ['roles', 'ddl'],
    queryFn: () => roleService.getAllRolesForDDL(),
    staleTime: 5 * 60_000,
  });
}

export function useUserMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: USERS_ROOT });

  const createUser = useMutation({ mutationFn: (data: CreateUserRequest) => userService.createUser(data), onSuccess: invalidate });
  const updateUser = useMutation({ mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) => userService.updateUser(id, data), onSuccess: invalidate });
  const toggleStatus = useMutation({ mutationFn: (id: string) => userService.toggleUserStatus(id), onSuccess: invalidate });
  const deleteUser = useMutation({ mutationFn: (id: string) => userService.deleteUser(id), onSuccess: invalidate });

  return { createUser, updateUser, toggleStatus, deleteUser };
}

export function useIssueLoginOtp() {
  return useMutation({
    mutationFn: (userId: string) => userService.issueLoginOtp(userId),
  });
}
